import { useEffect, useMemo, useRef, useState } from 'react';
import Fuse from 'fuse.js';
import './HelpSearch.css';

const RECENT_KEY = 'pc_help_recent_searches';
const MAX_RECENT = 5;

const loadRecent = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const saveRecent = (items) => {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(items));
  } catch (error) {
    // ignore storage failures
  }
};

const isTypingField = (target) =>
  ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName);

const buildSnippet = (text, startIndex, endIndex) => {
  const padding = 50;
  const start = Math.max(0, startIndex - padding);
  const end = Math.min(text.length, endIndex + padding);
  return {
    text: text.slice(start, end),
    highlightStart: startIndex - start,
    highlightEnd: endIndex - start + 1,
  };
};

const renderHighlightedText = (snippet) => {
  if (!snippet) return null;
  const { text, highlightStart, highlightEnd } = snippet;
  return (
    <span>
      {text.slice(0, highlightStart)}
      <mark>{text.slice(highlightStart, highlightEnd)}</mark>
      {text.slice(highlightEnd)}
    </span>
  );
};

export default function HelpSearch({ items, onSelectArticle, selectedArticleId }) {
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState(loadRecent);

  const fuse = useMemo(
    () =>
      new Fuse(items, {
        keys: ['title', 'content', 'keywords'],
        threshold: 0.3,
        includeScore: true,
        includeMatches: true,
        minMatchCharLength: 3,
        ignoreLocation: true,
      }),
    [items]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmed = query.trim();
      if (trimmed.length < 3) {
        setResults([]);
        return;
      }
      setResults(fuse.search(trimmed));
    }, 300);

    return () => clearTimeout(handler);
  }, [fuse, query]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === '/' && !isTypingField(event.target)) {
        event.preventDefault();
        inputRef.current?.focus();
      }

      if (event.key === 'Escape' && document.activeElement === inputRef.current) {
        setQuery('');
        setResults([]);
        inputRef.current.blur();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSelect = (item) => {
    if (!item) return;
    const trimmed = query.trim();
    if (trimmed) {
      const nextRecent = [trimmed, ...recentSearches.filter((term) => term !== trimmed)].slice(
        0,
        MAX_RECENT
      );
      setRecentSearches(nextRecent);
      saveRecent(nextRecent);
    }
    setQuery('');
    setResults([]);
    onSelectArticle(item.id);
  };

  const getSnippet = (result) => {
    const contentMatch = result.matches?.find((match) => match.key === 'content');
    const titleMatch = result.matches?.find((match) => match.key === 'title');
    const match = contentMatch || titleMatch;
    if (!match || !match.indices?.length) return null;
    const [start, end] = match.indices[0];
    const text = match.key === 'content' ? result.item.content : result.item.title;
    return buildSnippet(text, start, end);
  };

  const showRecent = query.trim().length === 0 && recentSearches.length > 0;

  return (
    <div className="help-search">
      <label className="help-search-label" htmlFor="help-search-input">
        Search help articles
      </label>
      <input
        id="help-search-input"
        ref={inputRef}
        className="help-search-input"
        placeholder="Search help articles..."
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Search help articles"
      />
      {query.trim().length >= 3 && (
        <div className="help-search-results" role="listbox">
          {results.length === 0 && (
            <div className="help-search-empty">No results found.</div>
          )}
          {results.map((result) => {
            const snippet = getSnippet(result);
            const isActive = result.item.id === selectedArticleId;
            return (
              <button
                key={result.item.id}
                type="button"
                className={`help-search-result${isActive ? ' is-active' : ''}`}
                onClick={() => handleSelect(result.item)}
                role="option"
                aria-selected={isActive}
              >
                <div className="help-search-result-title">{result.item.title}</div>
                <div className="help-search-result-meta">{result.item.categoryTitle}</div>
                {snippet && (
                  <div className="help-search-result-snippet">
                    {renderHighlightedText(snippet)}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
      {showRecent && (
        <div className="help-search-recent">
          <div className="help-search-recent-title">Recent searches</div>
          <div className="help-search-recent-list">
            {recentSearches.map((term) => (
              <button
                key={term}
                type="button"
                className="help-search-recent-item"
                onClick={() => setQuery(term)}
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
